import React, { useState } from 'react';
import { KeyRound, FileCheck2, RefreshCw, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { waApi, errMsg } from './waApi';

const TPL_LABEL = { ti_welcome: 'Welcome (first contact)', ti_daily_digest: 'Morning digest', ti_weekly_report: 'Weekly report' };
const tone = (s) => s === 'APPROVED' ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
  : s === 'PENDING' ? 'text-amber-300 border-amber-500/40 bg-amber-500/10'
  : s === 'REJECTED' ? 'text-rose-300 border-rose-500/40 bg-rose-500/10' : 'text-zinc-400 border-zinc-700 bg-zinc-950';

export const WaProductionReadiness = ({ status, onRefresh }) => {
  const [syncing, setSyncing] = useState(false);
  if (!status?.configured) return null;
  const tok = status.token || {};
  const tpl = status.templates || {};
  const tokenWarn = tok.permanent === false && (tok.hours_left ?? 999) < 48;

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await waApi.syncTemplates();
      if (r.ok) toast.success('Templates submitted to Meta — approval usually takes minutes to a few hours');
      else toast.error(r.error || 'Some templates failed — see status');
      onRefresh?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setSyncing(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="wa-production-readiness">
      <div className={`rounded-2xl border p-4 ${tokenWarn ? 'border-rose-500/40 bg-rose-500/5' : 'border-zinc-800 bg-zinc-900'}`} data-testid="wa-token-card">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100"><KeyRound size={14} className="text-emerald-400" /> Meta access token</div>
        {tok.error && (
          <div className={`text-xs mt-2 flex items-start gap-1 ${tok.blocked ? 'text-rose-300' : 'text-amber-300'}`} data-testid="wa-token-error">
            <AlertTriangle size={12} className="mt-0.5" />
            <span>{tok.blocked ? <><b>Meta has blocked API access for this app</b> (error 200). TI cannot send or receive until Meta lifts it — open the App Dashboard banner / Business Support Home and complete the requested verification or appeal.</> : <>Couldn't verify token: {tok.error}</>}</span>
          </div>
        )}
        {!tok.error && tok.permanent && <div className="text-xs text-emerald-300 mt-2 flex items-center gap-1"><CheckCircle2 size={12} /> Permanent system-user token · never expires</div>}
        {!tok.error && tok.permanent === false && (
          <div className={`text-xs mt-2 flex items-start gap-1 ${tokenWarn ? 'text-rose-300' : 'text-amber-300'}`}>
            <AlertTriangle size={12} className="mt-0.5" />
            <span>Temporary {tok.type?.toLowerCase()} token · expires in <b>{tok.hours_left} h</b>. TI goes silent after that — replace it with a permanent System User token (Business Settings → System Users → Generate token).</span>
          </div>
        )}
      </div>
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4" data-testid="wa-templates-card">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100"><FileCheck2 size={14} className="text-emerald-400" /> Message templates <span className="text-[10px] text-zinc-500 font-normal">reach people who haven't messaged TI in 24 h</span></div>
          <button onClick={sync} disabled={syncing || !tpl.waba_id_set} data-testid="wa-sync-templates"
            className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-xl border border-zinc-700 text-zinc-200 hover:border-emerald-500/50 disabled:opacity-40">
            <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> Sync to Meta
          </button>
        </div>
        {!tpl.waba_id_set && <div className="text-xs text-amber-300 mt-2">Add <code>META_WABA_ID</code> (WhatsApp Business Account ID from the API Setup page) to enable templates.</div>}
        <div className="flex flex-wrap gap-1.5 mt-2">
          {Object.entries(tpl.templates || {}).map(([name, t]) => (
            <span key={name} data-testid={`wa-template-${name}`} title={t.rejected_reason || ''}
              className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border ${tone(t.status)}`}>
              {t.status === 'PENDING' ? <Clock size={10} /> : null}{TPL_LABEL[name] || name} · {t.status}
            </span>
          ))}
        </div>
        {tpl.ready && <div className="text-[11px] text-emerald-300 mt-2">All templates approved — scheduled pushes are guaranteed for everyone.</div>}
      </div>
    </div>
  );
};
